import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getNews, type NewsItem } from '../api';
import PageHeader from '../components/PageHeader';

const TEAMS_FLAG: Record<string, string> = {
  ARG: '🇦🇷', AUS: '🇦🇺', AUT: '🇦🇹', BEL: '🇧🇪', BIH: '🇧🇦', BRA: '🇧🇷', CAN: '🇨🇦', CIV: '🇨🇮',
  COD: '🇨🇩', COL: '🇨🇴', CPV: '🇨🇻', CRO: '🇭🇷', CUW: '🇨🇼', CZE: '🇨🇿', ECU: '🇪🇨', EGY: '🇪🇬',
  ENG: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', ESP: '🇪🇸', FRA: '🇫🇷', GER: '🇩🇪', GHA: '🇬🇭', HAI: '🇭🇹', IRN: '🇮🇷', IRQ: '🇮🇶',
  JOR: '🇯🇴', JPN: '🇯🇵', KOR: '🇰🇷', KSA: '🇸🇦', MAR: '🇲🇦', MEX: '🇲🇽', NED: '🇳🇱', NOR: '🇳🇴',
  NZL: '🇳🇿', PAN: '🇵🇦', PAR: '🇵🇾', POR: '🇵🇹', QAT: '🇶🇦', RSA: '🇿🇦', SCO: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', SEN: '🇸🇳',
  SUI: '🇨🇭', SWE: '🇸🇪', TUN: '🇹🇳', TUR: '🇹🇷', URU: '🇺🇾', USA: '🇺🇸', UZB: '🇺🇿', ALG: '🇩🇿',
};

const SOURCE_LABELS: Record<string, string> = {
  espn: 'ESPN',
  espn_wc: 'ESPN · WC',
  espn_eng: 'ESPN · Premier League',
  espn_esp: 'ESPN · La Liga',
  espn_ita: 'ESPN · Serie A',
  espn_ger: 'ESPN · Bundesliga',
  espn_fra: 'ESPN · Ligue 1',
  espn_usa: 'ESPN · MLS',
  espn_uefa_cl: 'ESPN · Champions League',
  guardian_wc: 'Guardian · WC',
  guardian_football: 'Guardian · Football',
};

export default function News() {
  const [team, setTeam] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const { data, isLoading } = useQuery({
    queryKey: ['news', team],
    queryFn: () => getNews(team || undefined),
    refetchInterval: 5 * 60_000,
  });

  const items = useMemo(() => {
    const all = data?.items ?? [];
    return source ? all.filter((i) => i.source === source) : all;
  }, [data, source]);

  const sourcesInUse = useMemo(() => {
    const set = new Set<string>();
    for (const i of data?.items ?? []) set.add(i.source);
    return Array.from(set).sort((a, b) => (SOURCE_LABELS[a] ?? a).localeCompare(SOURCE_LABELS[b] ?? b));
  }, [data]);

  const teamOptions = useMemo(() => Object.keys(TEAMS_FLAG).sort(), []);

  return (
    <>
      <PageHeader
        title="News"
        subtitle={`World Cup intel — ESPN, Guardian, and top European leagues. ${data?.items.length ?? 0} stories.`}
        right={
          <select
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            className="h-9 bg-ink-800 border border-ink-700 rounded-lg px-2 text-xs text-chalk"
          >
            <option value="">All teams</option>
            {teamOptions.map((c) => <option key={c} value={c}>{TEAMS_FLAG[c]} {c}</option>)}
          </select>
        }
      />

      {sourcesInUse.length > 1 && (
        <div className="px-4 lg:px-0 mb-3 -mx-1 flex gap-1.5 overflow-x-auto pb-1">
          <SrcChip label="All sources" active={source === ''} onClick={() => setSource('')} count={data?.items.length ?? 0} />
          {sourcesInUse.map((s) => (
            <SrcChip
              key={s}
              label={SOURCE_LABELS[s] ?? s}
              active={source === s}
              onClick={() => setSource(s)}
              count={(data?.items ?? []).filter((i) => i.source === s).length}
            />
          ))}
        </div>
      )}

      <div className="px-4 lg:px-0 space-y-2.5">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card h-24 animate-pulse bg-ink-900/60" />
          ))
        ) : items.length === 0 ? (
          <div className="card p-6 text-center text-dim text-sm">No news matching this filter.</div>
        ) : (
          items.map((n) => <Article key={n.id} item={n} />)
        )}
      </div>
    </>
  );
}

function SrcChip({ label, active, onClick, count }: { label: string; active: boolean; onClick: () => void; count: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap transition-colors ${
        active ? 'bg-pitch-500/15 text-pitch-400 ring-1 ring-pitch-500/30' : 'bg-ink-800 text-dim hover:text-chalk'
      }`}
    >
      <span>{label}</span>
      <span className="text-dim">{count}</span>
    </button>
  );
}

function Article({ item }: { item: NewsItem }) {
  const teams: string[] = item.team_codes ? safeJSON(item.team_codes) : [];
  const sourceLabel = SOURCE_LABELS[item.source] ?? item.source;
  return (
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      className="card flex gap-3 p-3 hover:border-pitch-500/40 transition-colors"
    >
      {item.image_url && (
        <img
          src={item.image_url} alt=""
          loading="lazy"
          className="w-20 sm:w-28 h-20 sm:h-24 object-cover rounded-lg shrink-0"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-dim flex items-center gap-1.5 flex-wrap">
          <span className="text-pitch-400">{sourceLabel}</span>
          <span>·</span>
          <span>{relative(item.published_at)}</span>
        </div>
        <h3 className="text-sm font-semibold leading-snug mt-0.5 line-clamp-2">{item.title}</h3>
        {item.description && (
          <p className="text-xs text-dim mt-1 line-clamp-3">{item.description}</p>
        )}
        {teams.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {teams.slice(0, 6).map((code) => (
              <span key={code} className="pill bg-ink-800 text-chalk text-[10px]">
                {TEAMS_FLAG[code] ?? '🏳️'} {code}
              </span>
            ))}
            {teams.length > 6 && <span className="text-[10px] text-dim self-center">+{teams.length - 6}</span>}
          </div>
        )}
      </div>
    </a>
  );
}

function safeJSON(s: string): string[] {
  try { return JSON.parse(s); } catch { return []; }
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
